import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/i18n";

export const Header = () => {
  const { t, lang, switchLang, localizedPath } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const homeHref = localizedPath("");

  const navLinks = [
    { key: "home", to: homeHref, hash: "", label: t("header.nav_home") },
    { key: "sector", to: `${homeHref}#sectors`, hash: "sectors", label: t("header.nav_sector") },
    { key: "methodology", to: `${homeHref}#methodology`, hash: "methodology", label: t("header.nav_methodology") },
    { key: "transparency", to: `${homeHref}#transparency`, hash: "transparency", label: t("header.nav_transparency") },
  ];

  const subscribeTo = `${homeHref}#newsletter`;
  const closeMenu = () => setOpen(false);

  // Ensure nav works even when clicking the same section again from Home:
  // if already on the target page, imperatively navigate to force a location update.
  const handleNav = (to, targetHash) => (e) => {
    if (location.pathname === homeHref) {
      e.preventDefault();
      if (targetHash) {
        const el = document.getElementById(targetHash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", `${homeHref}#${targetHash}`);
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        window.history.replaceState(null, "", homeHref);
      }
      closeMenu();
      return;
    }
    e.preventDefault();
    navigate(to);
    closeMenu();
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full border-b border-slate-200 bg-[#0F2C59] text-white"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <Link
          to={homeHref}
          data-testid="header-logo"
          onClick={closeMenu}
          className="flex items-center gap-2 no-underline"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-sm bg-[#C85A32] font-serif text-lg font-bold text-white">
            Z
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-serif text-lg font-bold tracking-wide text-white">
              ZELVONIQ
            </span>
            <span className="mt-0.5 text-[10px] uppercase tracking-[0.22em] text-slate-300">
              {t("meta.site_tagline")}
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="primary">
          {navLinks.map((l) => (
            <Link
              key={l.key}
              to={l.to}
              onClick={handleNav(l.to, l.hash)}
              data-testid={`nav-link-${l.key}`}
              className="text-sm font-medium tracking-wide text-slate-200 hover:text-white"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <div className="flex items-center gap-1 rounded-sm border border-slate-500 px-1 py-1 text-xs font-semibold">
            <button
              type="button"
              onClick={() => switchLang("ar")}
              data-testid="language-switcher-es"
              className={`px-2 py-1 ${lang === "ar" ? "bg-white text-[#0F2C59]" : "text-slate-200 hover:text-white"}`}
              aria-label={t("meta.lang_label_es")}
            >
              {t("meta.lang_short_es")}
            </button>
            <button
              type="button"
              onClick={() => switchLang("en")}
              data-testid="language-switcher-en"
              className={`px-2 py-1 ${lang === "en" ? "bg-white text-[#0F2C59]" : "text-slate-200 hover:text-white"}`}
              aria-label={t("meta.lang_label_en")}
            >
              {t("meta.lang_short_en")}
            </button>
          </div>
          <Link
            to={subscribeTo}
            onClick={handleNav(subscribeTo, "newsletter")}
            data-testid="header-cta-subscribe"
            className="inline-flex items-center rounded-sm bg-[#C85A32] px-4 py-2 text-sm font-semibold text-white hover:bg-[#a94a28]"
          >
            {t("header.cta_subscribe")}
          </Link>
        </div>

        <button
          type="button"
          className="inline-flex items-center justify-center rounded-sm border border-slate-500 p-2 text-white lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? t("header.close_menu") : t("header.open_menu")}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div
          className="border-t border-slate-700 bg-[#0F2C59] lg:hidden"
          data-testid="mobile-menu"
        >
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-4 sm:px-6">
            {navLinks.map((l) => (
              <Link
                key={`m-${l.key}`}
                to={l.to}
                onClick={handleNav(l.to, l.hash)}
                data-testid={`mobile-nav-link-${l.key}`}
                className="rounded-sm px-2 py-3 text-base font-medium text-slate-100 hover:bg-slate-800"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to={subscribeTo}
              onClick={handleNav(subscribeTo, "newsletter")}
              data-testid="mobile-cta-subscribe"
              className="mt-2 inline-flex items-center justify-center rounded-sm bg-[#C85A32] px-4 py-3 text-sm font-semibold text-white"
            >
              {t("header.cta_subscribe")}
            </Link>
            <div className="mt-3 flex items-center gap-2 border-t border-slate-700 pt-3">
              <button
                type="button"
                onClick={() => {
                  switchLang("ar");
                  closeMenu();
                }}
                data-testid="mobile-language-switcher-es"
                className={`flex-1 rounded-sm border border-slate-600 px-3 py-2 text-sm font-semibold ${
                  lang === "ar" ? "bg-white text-[#0F2C59]" : "text-slate-100"
                }`}
              >
                {t("meta.lang_short_es")} — {t("meta.lang_label_es")}
              </button>
              <button
                type="button"
                onClick={() => {
                  switchLang("en");
                  closeMenu();
                }}
                data-testid="mobile-language-switcher-en"
                className={`flex-1 rounded-sm border border-slate-600 px-3 py-2 text-sm font-semibold ${
                  lang === "en" ? "bg-white text-[#0F2C59]" : "text-slate-100"
                }`}
              >
                {t("meta.lang_short_en")} — {t("meta.lang_label_en")}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
